import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CatalogNativeSelect from "./CatalogNativeSelect";
import { getFeedColumnLabel } from "./utils/catalogMappingTableUtils";
import {
  COLUMN_DEFAULT_MODE_ALWAYS,
  COLUMN_DEFAULT_MODE_OPTIONS,
  addRuleToGroups,
  flattenColumnDefaultGroups,
  formatColumnDefaultRuleCardLabel,
  normalizeColumnDefaultGroups,
  removeRuleFromGroups,
} from "./utils/columnDefaultRulesUtils";
import { CM_TOOLTIPS } from "./utils/tooltipsContent";
import { buildMappingTargetFieldOptions } from "./utils/mappingTokenCategoryUtils";

const ColumnDefaultsPanel = ({
  columnDefaults,
  fieldKeys,
  columnNumbers,
  headerLabels,
  isHeadless,
  mapping = {},
  onColumnDefaultsChange,
  disabled = false,
  className = "",
  showMatchesOnly = false,
  onShowMatchesOnlyChange,
}) => {
  const [feedColumn, setFeedColumn] = useState("");
  const [targetField, setTargetField] = useState("");
  const [mode, setMode] = useState(COLUMN_DEFAULT_MODE_ALWAYS);
  const [matchValue, setMatchValue] = useState("");
  const [defaultValue, setDefaultValue] = useState("");

  const groupedRules = useMemo(
    () => normalizeColumnDefaultGroups(columnDefaults),
    [columnDefaults],
  );
  const flatRules = useMemo(
    () => flattenColumnDefaultGroups(groupedRules),
    [groupedRules],
  );

  const fieldOptions = useMemo(
    () => buildMappingTargetFieldOptions({
      fieldKeys,
      mapping,
      columnDefaultRules: columnDefaults,
      currentValue: targetField,
    }),
    [fieldKeys, mapping, columnDefaults, targetField],
  );
  const isAlwaysMode = mode === COLUMN_DEFAULT_MODE_ALWAYS;

  const feedColumnOptions = useMemo(() => {
    const columns = columnNumbers.map((column) => ({
      value: column,
      label: getFeedColumnLabel(column, headerLabels, isHeadless),
    }));
    if (isAlwaysMode) {
      return [{ value: "", label: "-" }, ...columns];
    }
    return columns;
  }, [columnNumbers, headerLabels, isHeadless, isAlwaysMode]);
  const canAddRule = Boolean(targetField)
    && String(defaultValue ?? "").trim() !== ""
    && !disabled
    && (isAlwaysMode || String(feedColumn || "").trim());

  const handleModeChange = (nextMode) => {
    setMode(nextMode);
    if (nextMode === COLUMN_DEFAULT_MODE_ALWAYS) {
      setFeedColumn("");
      setMatchValue("");
    }
  };

  const resetForm = () => {
    setMatchValue("");
    setDefaultValue("");
  };

  const handleAddRule = () => {
    if (!canAddRule) {
      return;
    }
    const nextRule = {
      feed_column: String(feedColumn || "").trim(),
      target_field: String(targetField).trim(),
      mode,
      match: isAlwaysMode ? "" : String(matchValue ?? ""),
      value: String(defaultValue ?? "").trim(),
    };
    const exists = flatRules.some(
      (rule) => rule.feed_column === nextRule.feed_column
        && rule.target_field === nextRule.target_field
        && rule.mode === nextRule.mode
        && rule.match === nextRule.match
        && rule.value === nextRule.value,
    );
    if (exists) {
      return;
    }
    onColumnDefaultsChange(addRuleToGroups(groupedRules, nextRule));
    resetForm();
  };

  const handleRemoveRule = (target, index) => {
    if (disabled) {
      return;
    }
    onColumnDefaultsChange(removeRuleFromGroups(groupedRules, target, index));
  };

  return (
    <div className={`cm-column-defaults-panel${className ? ` ${className}` : ""}`.trim()}>
      <div className="cm-column-defaults-form">
        <span className="cm-column-defaults-heading" aria-hidden="true" title={CM_TOOLTIPS.fieldDefaults}>
          <span>SET</span>
          <span>DEF</span>
        </span>
        <CatalogNativeSelect
          className="cm-column-defaults-feed-select"
          value={isAlwaysMode ? "" : feedColumn}
          onChange={setFeedColumn}
          options={feedColumnOptions}
          placeholder="Colonna feed"
          ariaLabel="Colonna feed matching"
          disabled={disabled || isAlwaysMode}
        />
        <CatalogNativeSelect
          className="cm-column-defaults-target-select"
          value={targetField}
          onChange={setTargetField}
          options={fieldOptions}
          placeholder="Mapping token"
          ariaLabel="Colonna DB destinazione"
          disabled={disabled}
        />
        <CatalogNativeSelect
          className="cm-cell-match-mode-select"
          value={mode}
          onChange={handleModeChange}
          options={COLUMN_DEFAULT_MODE_OPTIONS}
          placeholder="Mode"
          ariaLabel="Modalita default colonna"
          disabled={disabled}
        />
        <input
          type="text"
          className="cm-input cm-column-defaults-match"
          placeholder={isAlwaysMode ? "-" : "Valore match"}
          value={matchValue}
          disabled={disabled || isAlwaysMode}
          onChange={(event) => setMatchValue(event.target.value)}
        />
        <input
          type="text"
          className="cm-input cm-column-defaults-value"
          placeholder="Valore default"
          value={defaultValue}
          disabled={disabled}
          onChange={(event) => setDefaultValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              handleAddRule();
            }
          }}
        />
        <button
          type="button"
          className="cm-btn cm-btn-secondary cm-column-defaults-add-btn"
          onClick={handleAddRule}
          disabled={!canAddRule}
          title={CM_TOOLTIPS.aggiungiFieldDefault}
        >
          Aggiungi
        </button>
        <button
          type="button"
          className={`cm-btn cm-btn-secondary cm-tool-matches-btn${showMatchesOnly ? " is-active" : ""}`}
          onClick={() => onShowMatchesOnlyChange?.(!showMatchesOnly)}
          disabled={disabled || flatRules.length === 0 || !onShowMatchesOnlyChange}
          title="Mostra solo righe con celle che matchano regole SET DEF"
        >
          Solo matches
        </button>
      </div>

      {flatRules.length > 0 && (
        <div className="cm-column-defaults-rules">
          {Object.entries(groupedRules).map(([target, rules]) => rules.map((rule, index) => (
            <div key={`${target}-${rule.mode}-${index}`} className="cm-column-defaults-card">
              <span className="cm-column-defaults-card-label">
                {formatColumnDefaultRuleCardLabel(rule, target, {
                  feedColumnLabel: rule.feed_column
                    ? getFeedColumnLabel(rule.feed_column, headerLabels, isHeadless)
                    : "",
                })}
              </span>
              <IconButton
                size="small"
                aria-label="Rimuovi regola default colonna"
                title={CM_TOOLTIPS.rimuoviFieldDefault}
                disabled={disabled}
                onClick={() => handleRemoveRule(target, index)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          )))}
        </div>
      )}
    </div>
  );
};

ColumnDefaultsPanel.propTypes = {
  columnDefaults: PropTypes.object,
  fieldKeys: PropTypes.arrayOf(PropTypes.string),
  columnNumbers: PropTypes.arrayOf(PropTypes.string).isRequired,
  headerLabels: PropTypes.object,
  isHeadless: PropTypes.bool,
  mapping: PropTypes.object,
  onColumnDefaultsChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  showMatchesOnly: PropTypes.bool,
  onShowMatchesOnlyChange: PropTypes.func,
};

export default ColumnDefaultsPanel;
